import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { Box, Button, Stack, type ButtonProps } from "@mui/material";
import { MerchantFacilities } from "Components/Hunter/Merchants/CompleteRegistration/Tabs/MerchantFacilities/MerchantFacilities";
import { MerchantNotes } from "Components/Hunter/Merchants/CompleteRegistration/Tabs/MerchantNotes/MerchantNotes";
import { MerchantParty } from "Components/Hunter/Merchants/CompleteRegistration/Tabs/MerchantParty/MerchantParty";
import { SendAccess } from "Components/Hunter/Merchants/CompleteRegistration/Tabs/SendAccess/SendAccess";
import { useMerchantFacilityInfo } from "Components/Shared/Merchant/Facilities/hooks/useMerchantFacilityInfo";
import type { MerchantCompleteRegistrationPageStepID } from "Components/Hunter/Merchants/CompleteRegistration/steps";
import type { MerchantCompleteRegistrationStep } from "Components/Hunter/Merchants/CompleteRegistration/types";
import { AppProducts } from "Components/Shared/Merchant/Products/AppProducts/AppProducts";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { type MerchantDetailDto, PartyType } from "Infrastructure/Api/Api";
import { hasFacilityPhotos } from "Components/Shared/Merchant/Facilities/utils";
import { useSearchParams } from "Navigation/useSearchParams";
import { getRegionCurrency } from "Utils/CurrencyUtils";
import { Resources, useResource } from "Translations/Resources";
import type { ReactNode } from "react";

const EmptySpace = () => <div aria-hidden="true" />;

const StepperControlsContainer = ({ children }: { children: ReactNode }) => (
  <Box width="100%" display="flex" justifyContent="space-between">
    {children}
  </Box>
);

type StepDefinition = { content: ReactNode; stepperControls: ReactNode };

type Props = {
  activeStepIndex: number;
  merchantDetail: MerchantDetailDto;
  steps: ReadonlyArray<MerchantCompleteRegistrationStep>;
};

export const MerchantCompleteRegistrationPageStepNavigator = ({
  steps,
  merchantDetail,
  activeStepIndex,
}: Props) => {
  const [_, setSearchParams] = useSearchParams();

  const { t } = useResource();

  const goToNextStep = () => {
    setSearchParams({ activeStep: steps[activeStepIndex + 1].id });
  };

  const goToPreviousStep = () => {
    setSearchParams({ activeStep: steps[activeStepIndex - 1].id });
  };

  const formId = `${steps[activeStepIndex].id}-form`;
  const nextButtonProps: ButtonProps = {
    children: t(Resources.Hunter.Merchants.CompleteRegistration.NextStep),
    variant: "contained",
    endIcon: <KeyboardArrowRight />,
    sx: {
      "&.Mui-disabled": {
        color: ({ palette }) => palette.common.white,
      },
    },
  };

  const formControllingNextButtonProps: ButtonProps = {
    ...nextButtonProps,
    type: "submit",
    form: formId,
  };

  const basicNextStepButtonProps: ButtonProps = {
    ...nextButtonProps,
    type: "button",
    disabled: !steps[activeStepIndex].isCompleted,
    onClick: goToNextStep,
  };

  const backButtonProps: ButtonProps = {
    children: t(Resources.Hunter.Merchants.CompleteRegistration.PreviousStep),
    startIcon: <KeyboardArrowLeft />,
    onClick: goToPreviousStep,
  };

  const isLegalEntity = merchantDetail.party.type === PartyType.LegalEntity;
  const { isRequired: requiresFacility } = useMerchantFacilityInfo(
    merchantDetail.publicID,
  );
  const hasFacilities = merchantDetail.facilities.length > 0;
  const allFacilitiesComplete =
    hasFacilities && merchantDetail.facilities.every(hasFacilityPhotos);

  const stepContents: Record<
    MerchantCompleteRegistrationPageStepID,
    StepDefinition
  > = {
    party: {
      content: (
        <MerchantParty
          formId={formId}
          merchantPublicID={merchantDetail.publicID}
          onAfterSubmit={goToNextStep}
        />
      ),
      stepperControls: (
        <StepperControlsContainer>
          <EmptySpace />
          <Button
            {...(isLegalEntity
              ? basicNextStepButtonProps
              : formControllingNextButtonProps)}
          />
        </StepperControlsContainer>
      ),
    },
    notes: {
      content: (
        <MerchantNotes
          formId={formId}
          merchantPublicID={merchantDetail.publicID}
          onAfterSubmit={goToNextStep}
        />
      ),
      stepperControls: (
        <StepperControlsContainer>
          <EmptySpace />
          <Button {...formControllingNextButtonProps} />
        </StepperControlsContainer>
      ),
    },
    facilities: {
      content: (
        <MerchantFacilities
          merchantPublicID={merchantDetail.publicID}
          facilities={merchantDetail.facilities}
          isRequired={requiresFacility}
          partyPublicID={merchantDetail.party.publicID}
          marketCurrencyCode={
            merchantDetail.party.region
              ? getRegionCurrency(merchantDetail.party.region)
              : undefined
          }
        />
      ),
      stepperControls: (
        <StepperControlsContainer>
          <Button {...backButtonProps} />
          <Button
            {...nextButtonProps}
            type="button"
            disabled={
              (requiresFacility && !hasFacilities) ||
              (hasFacilities && !allFacilitiesComplete)
            }
            onClick={goToNextStep}
          >
            {!hasFacilities && !requiresFacility
              ? t(
                  Resources.Hunter.Merchants.CompleteRegistration.Facilities
                    .ContinueWithoutFacility,
                )
              : t(Resources.Hunter.Merchants.CompleteRegistration.NextStep)}
          </Button>
        </StepperControlsContainer>
      ),
    },
    products: {
      content: (
        <AppProducts
          merchantPublicID={merchantDetail.publicID}
          variant="editable"
          isOnboarding
        />
      ),
      stepperControls: (
        <StepperControlsContainer>
          <Button {...backButtonProps} />
          <Button {...basicNextStepButtonProps} />
        </StepperControlsContainer>
      ),
    },
    completion: {
      content: (
        <SendAccess
          merchantPublicID={merchantDetail.publicID}
          publicPartyID={merchantDetail.party.publicID || ""}
        />
      ),
      stepperControls: (
        <StepperControlsContainer>
          <Button {...backButtonProps}>
            {t(Resources.Hunter.Merchants.CompleteRegistration.PreviousStep)}
          </Button>
          <EmptySpace />
        </StepperControlsContainer>
      ),
    },
  };

  const activeStepId = steps[activeStepIndex].id;
  const activeStepContent = stepContents[activeStepId];

  return (
    <Stack gap={3}>
      <PrimaryPaper>{activeStepContent.content}</PrimaryPaper>
      {activeStepContent.stepperControls}
    </Stack>
  );
};
